/**
 * Centralized route definitions
 * 
 * This file contains all route paths used throughout the application.
 * Using TypeScript const assertions ensures type-safe navigation.
 * Both App.tsx and sidebar components import from this file.
 * 
 * @example
 * // In components (e.g., sidebars):
 * import { ORG_ROUTES } from '@/routes/routes';
 * const navItems = [
 *   { title: "Dashboard", url: ORG_ROUTES.dashboard, icon: LayoutDashboard },
 * ];
 * 
 * // In App.tsx for route definitions:
 * import { ORG_ROUTES } from '@/routes/routes';
 * <Route path={ORG_ROUTES.dashboard} element={<OrgDashboard />} />
 * 
 * // For navigation:
 * import { ORG_ROUTES } from '@/routes/routes';
 * navigate(ORG_ROUTES.dashboard);
 */

// Public routes
export const PUBLIC_ROUTES = {
  home: '/',
  landing: '/landing',
  login: '/login',
  signup: '/signup',
  orgSignup: '/org/signup',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  verify: '/verify',
  pricing: '/pricing',
  privacy: '/privacy',
  privacyPolicy: '/privacy', // Alias for privacy route for clarity
  terms: '/terms',
  faq: '/faq',
  support: '/faq', // Support links to FAQ page which has contact support
  guest: '/guest',
  formToken: '/form/:token',
  authCallback: '/auth/callback',
  logout: '/logout',
  confirmEmailChange: '/confirm-email-change',
  buildVersion: '/__version',
} as const;

// Organization routes
export const ORG_ROUTES = {
  dashboard: '/org/dashboard',
  submissions: '/org/submissions',
  submissionDetail: '/org/submissions/:id',
  invitations: '/org/invitations',
  templates: '/org/templates',
  templatePublish: '/org/templates/:templateId/publish',
  editor: '/org/editor',
  settings: '/org/settings',
  settingsBilling: '/org/settings/billing',
  settingsBillingWildcard: '/org/settings/billing/*',
  billing: '/org/billing',
  analytics: '/org/analytics',
  audit: '/org/audit',
  team: '/org/team',
  // Note: compliance is at root level for historical reasons, not under /org
  compliance: '/compliance',
} as const;

// Profile routes (individual users)
export const PROFILE_ROUTES = {
  dashboard: '/profile/dashboard',
  vault: '/profile/vault',
  settings: '/profile/settings',
  account: '/profile/account',
  family: '/profile/family',
  familyMemberNew: '/profile/family-member/new',
  familyMemberEdit: '/profile/family-member/edit/:id',
  applySlug: '/profile/apply/:slug',
  application: '/profile/application',
  applicationSchool: '/profile/application/school',
  forms: '/profile/forms',
  formDetail: '/profile/forms/:id',
  applications: '/profile/applications', // Legacy redirect
  applicationsId: '/profile/applications/:id', // Legacy redirect
  invitations: '/profile/invitations', // Legacy redirect
  invitationsToken: '/profile/invitations/:token', // Legacy route
  reminders: '/profile/reminders',
  upload: '/profile/upload',
  billing: '/profile/billing',
  analytics: '/profile/analytics',
  dataExport: '/profile/data-export',
  delegates: '/profile/delegates',
  share: '/profile/share',
  consents: '/profile/consents',
} as const;

// Admin routes
export const ADMIN_ROUTES = {
  root: '/admin',
  overview: '/admin/overview',
  organizations: '/admin/organizations',
  billing: '/admin/billing',
  events: '/admin/events',
  usage: '/admin/usage',
  funnel: '/admin/funnel',
  diagnostics: '/admin/diagnostics',
  settings: '/admin/settings',
  dataRequests: '/admin/data-requests',
} as const;

// Account settings routes
export const ACCOUNT_ROUTES = {
  settings: '/settings',
  // Removed accountSettings - consolidated with PROFILE_ROUTES.account
  security: '/settings/security',
  securityMfaSetup: '/settings/security/mfa-setup',
  billing: '/settings/billing',
} as const;

// Form and application routes
export const FORM_ROUTES = {
  formEditor: '/form-editor/:token',
  formInvitation: '/form-invitation/:token',
  applyDemoOrg: '/apply/demo-org/demo-template',
  applyOrgTemplate: '/apply/:orgSlug/:templateSlug',
} as const;

// Dev routes
export const DEV_ROUTES = {
  embedHarness: '/dev/embed-harness',
  embedFrame: '/dev/embed-frame',
  analytics: '/dev/analytics',
} as const;

// Auth routes
export const AUTH_ROUTES = {
  accountTypeSelection: '/account-type-selection',
  setup: '/setup',
} as const;

// All routes combined for reference
export const ROUTES = {
  public: PUBLIC_ROUTES,
  org: ORG_ROUTES,
  profile: PROFILE_ROUTES,
  admin: ADMIN_ROUTES,
  account: ACCOUNT_ROUTES,
  form: FORM_ROUTES,
  dev: DEV_ROUTES,
  auth: AUTH_ROUTES,
} as const;
